import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useJournalEntry } from "@/hooks/journal/useJournalEntry";
import journalPrompts from "@/data/journalPrompts";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/SafeAuthProvider";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import JournalEntryHeader from "@/components/journal/JournalEntryHeader";
import JournalEntryProgress from "@/components/journal/JournalEntryProgress";
import JournalEntryContent from "@/components/journal/JournalEntryContent";

const JournalEntryPage = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  const {
    journalEntry,
    fetchJournalEntry,
    deleteEntry
  } = useJournalEntry(journalPrompts);

  useEffect(() => {
    if (entryId) {
      fetchJournalEntry(entryId);
    }
  }, [entryId, fetchJournalEntry]);

  const handleDeleteEntry = async () => {
    if (!entryId || !user) return;
    await deleteEntry(entryId);
  };

  const handleProgressUpdate = async (newProgress: number): Promise<boolean> => {
    if (!entryId || !user) return false;

    try {
      const { error } = await supabase
        .from("journal_entries")
        .update({ 
          progress: newProgress,
          updated_at: new Date().toISOString() 
        })
        .eq("id", entryId)
        .eq("user_id", user.id);

      if (error) {
        console.error("Error updating progress:", error);
        toast({
          title: "Error",
          description: "Failed to update progress",
          variant: "destructive"
        });
        return false;
      }

      // Refetch the journal entry to update the UI
      await fetchJournalEntry(entryId);
      toast({
        title: enableRpgMode ? "Quest updated" : "Progress updated",
        description: enableRpgMode ? "Your quest progress has been updated" : "Your progress has been updated"
      });
      return true;
    } catch (error) {
      console.error("Error updating progress:", error);
      return false;
    }
  };

  const handleCompleteProgress = async (): Promise<boolean> => {
    if (!journalEntry) return false;
    return handleProgressUpdate(journalEntry.progress_max || 7);
  };
  
  const handlePipClick = async (pipValue: number): Promise<boolean> => {
    if (!journalEntry) return false;
    return handleProgressUpdate(pipValue);
  };

  if (!journalEntry) {
    return (
      <div className={cn(
        "container py-8",
        enableRpgMode && rpgTheme.background + " min-h-screen"
      )}>
        <Card className={cn(
          enableRpgMode && rpgTheme.cardBackground
        )}>
          <CardContent className="pt-6">
            <div className="flex justify-center items-center h-40">
              <p className={cn(enableRpgMode && rpgTheme.text)}>
                {enableRpgMode ? "Summoning quest details..." : "Loading journal entry..."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const diceValue = journalEntry.dice_value;
  const currentPrompts = journalPrompts[diceValue as keyof typeof journalPrompts];
  const progress = journalEntry.progress || 0;
  const maxProgress = journalEntry.progress_max || 7;

  return (
    <div className={cn(
      "container py-8",
      enableRpgMode && rpgTheme.background + " min-h-screen"
    )}>
      <JournalEntryHeader 
        entryId={entryId}
        journalEntry={journalEntry}
        onDeleteEntry={handleDeleteEntry}
      />

      <JournalEntryProgress 
        progress={progress}
        maxProgress={maxProgress}
        onCompleteProgress={handleCompleteProgress}
        onPipClick={handlePipClick}
        difficultyRank={journalEntry.difficulty_rank}
      />
      
      <JournalEntryContent 
        journalEntry={journalEntry}
        currentPrompts={currentPrompts}
        entryId={entryId}
      />
    </div>
  );
};

export default JournalEntryPage;
